import { Alert, AlertColor, Button, Snackbar } from '@mui/material';
import React, { createContext, useCallback, useContext, useState, ReactNode } from 'react';

/** Optional call-to-action rendered inside the toast (e.g. "Complete profile"). */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

// Define the shape of our Toast Context
interface ToastContextType {
  showToast: (message: string, severity?: AlertColor, action?: ToastAction) => void;
  hideToast: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<AlertColor>('info');
  const [action, setAction] = useState<ToastAction | null>(null);

  // useCallback keeps these stable so callers can safely list them in effect deps.
  const showToast = useCallback((msg: string, type: AlertColor = 'info', act?: ToastAction) => {
    setMessage(msg);
    setSeverity(type);
    setAction(act ?? null);
    setOpen(true);
  }, []);

  const hideToast = useCallback(() => {
    setOpen(false);
  }, []);

  const handleAction = () => {
    action?.onClick();
    hideToast();
  };

  return (
    <ToastContext.Provider value={{ showToast, hideToast }}>
      {children}
      <Snackbar
        open={open}
        // Actionable toasts stay a little longer so there's time to click; plain ones stay snappy.
        autoHideDuration={action ? 10000 : 4000}
        onClose={hideToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }} // Top-Right corner
      >
        <Alert
          onClose={hideToast}
          severity={severity}
          variant="filled"
          sx={{ width: '100%' }}
          action={
            action ? (
              <Button color="inherit" size="small" onClick={handleAction} sx={{ fontWeight: 700 }}>
                {action.label}
              </Button>
            ) : undefined
          }
        >
          {message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
};

// Custom Hook
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
